// Investment Strategy PDF Generator
// Generates ATO-compliant SMSF investment strategy documents
// Complies with Regulation 4.09 SIS Act 1993

import jsPDF from 'jspdf';

export const generateInvestmentStrategyPDF = (strategyData) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 20;
    const contentWidth = pageWidth - (2 * margin);
    let currentY = margin;
    
    // Helper functions
    const addText = (text, x, y, options = {}) => {
        const {
            size = 10,
            style = 'normal',
            align = 'left',
            color = [0, 0, 0],
            maxWidth = contentWidth
        } = options;
        
        pdf.setFontSize(size);
        pdf.setFont('helvetica', style);
        pdf.setTextColor(...color);
        
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line, idx) => {
            if (y + (idx * 6) > pageHeight - margin) {
                pdf.addPage();
                y = margin;
                currentY = margin;
            }
            pdf.text(line, x, y + (idx * 6), { align });
        });
        
        return y + (lines.length * 6);
    };
    
    const addHeading = (text, level = 1) => {
        if (currentY > pageHeight - margin - 20) {
            pdf.addPage();
            currentY = margin;
        }
        
        const sizes = { 1: 18, 2: 14, 3: 12 };
        const colors = { 1: [0, 51, 153], 2: [51, 51, 51], 3: [85, 85, 85] };
        
        currentY = addText(text, margin, currentY, {
            size: sizes[level],
            style: 'bold',
            color: colors[level]
        });
        currentY += 5;
    };
    
    const addParagraph = (text) => {
        currentY = addText(text, margin, currentY, { size: 10 });
        currentY += 4;
    };
    
    const addBullet = (text, indent = 0) => {
        const bulletX = margin + indent;
        pdf.circle(bulletX + 2, currentY - 1.5, 1, 'F');
        currentY = addText(text, bulletX + 5, currentY, { size: 10, maxWidth: contentWidth - indent - 5 });
        currentY += 2;
    };
    
    const addTable = (headers, rows) => {
        const colWidth = contentWidth / headers.length;
        const rowHeight = 8;
        
        // Check if table fits on page
        const tableHeight = (rows.length + 1) * rowHeight;
        if (currentY + tableHeight > pageHeight - margin) {
            pdf.addPage();
            currentY = margin;
        }
        
        // Header
        pdf.setFillColor(200, 200, 200);
        pdf.rect(margin, currentY, contentWidth, rowHeight, 'F');
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        headers.forEach((header, idx) => {
            pdf.text(header, margin + (idx * colWidth) + 2, currentY + 6);
        });
        currentY += rowHeight;
        
        // Rows
        pdf.setFont('helvetica', 'normal');
        rows.forEach((row, ridx) => {
            if (ridx % 2 === 0) {
                pdf.setFillColor(245, 245, 245);
                pdf.rect(margin, currentY, contentWidth, rowHeight, 'F');
            }
            row.forEach((cell, cidx) => {
                pdf.text(String(cell), margin + (cidx * colWidth) + 2, currentY + 6);
            });
            currentY += rowHeight;
        });
        
        currentY += 5;
    };
    
    const addBox = (title, content, color = [200, 230, 255]) => {
        if (currentY > pageHeight - margin - 40) {
            pdf.addPage();
            currentY = margin;
        }
        
        pdf.setFillColor(...color);
        const boxHeight = 10 + (content.split('\n').length * 5);
        pdf.rect(margin, currentY, contentWidth, boxHeight, 'F');
        pdf.setDrawColor(100, 100, 100);
        pdf.rect(margin, currentY, contentWidth, boxHeight);
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(title, margin + 3, currentY + 7);
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        content.split('\n').forEach((line, idx) => {
            pdf.text(line, margin + 3, currentY + 14 + (idx * 5));
        });
        
        currentY += boxHeight + 5;
    };
    
    // ========================================================================
    // DOCUMENT GENERATION
    // ========================================================================
    
    // Page 1: Cover Page
    pdf.setFillColor(0, 51, 153);
    pdf.rect(0, 0, pageWidth, 80, 'F');
    
    pdf.setFontSize(26);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text('SMSF INVESTMENT STRATEGY', pageWidth / 2, 35, { align: 'center' });
    
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    pdf.text(strategyData.fundDetails.fundName || 'Fund Name', pageWidth / 2, 50, { align: 'center' });
    pdf.text(`ABN: ${strategyData.fundDetails.abn || 'XXX XXX XXX'}`, pageWidth / 2, 60, { align: 'center' });
    
    currentY = 100;
    pdf.setTextColor(0, 0, 0);
    
    addBox(
        'DOCUMENT STATUS',
        `Prepared: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}\n` +
        `Next Review: ${strategyData.review.nextReviewDate ? new Date(strategyData.review.nextReviewDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not set'}\n` +
        `Compliance Status: ${strategyData.compliance?.allElementsAddressed ? 'COMPLIANT' : 'REQUIRES REVIEW'}`,
        [144, 238, 144]
    );
    
    addBox(
        '⚖️ LEGAL REQUIREMENT',
        'This investment strategy is prepared in accordance with Regulation 4.09 of the Superannuation Industry\n' +
        '(Supervision) Act 1993 (SIS Act). All SMSF trustees are legally required to formulate, review, and\n' +
        'give effect to an investment strategy for the fund.',
        [255, 235, 235]
    );
    
    addHeading('TABLE OF CONTENTS');
    const toc = [
        '1. Member Circumstances',
        '2. Investment Objectives',
        '3. Risk Profile',
        '4. Asset Allocation Strategy',
        '5. Diversification Analysis',
        '6. Liquidity Management',
        '7. Insurance Consideration',
        '8. Review and Monitoring'
    ];
    toc.forEach(item => addBullet(item));
    
    // Page 2: Member Circumstances
    pdf.addPage();
    currentY = margin;
    
    addHeading('1. MEMBER CIRCUMSTANCES');
    addParagraph('The following members are trustees/members of the fund:');
    
    if (strategyData.members && strategyData.members.length > 0) {
        strategyData.members.forEach((member, idx) => {
            addHeading(`Member ${idx + 1}: ${member.name || 'Not specified'}`, 3);
            
            const memberDetails = [
                `Age: ${member.age || 'Not specified'} years`,
                `Employment Status: ${member.employmentStatus || 'Not specified'}`,
                `Current Balance: $${Number(member.balance || 0).toLocaleString()}`,
                `Risk Tolerance: ${member.riskTolerance || 'Not specified'}`,
                `Investment Objectives: ${member.objectives || 'Not specified'}`,
                `Dependents: ${member.dependents || 0}`,
                `External Super: $${Number(member.externalSuper || 0).toLocaleString()}`
            ];
            
            memberDetails.forEach(detail => addBullet(detail, 5));
            currentY += 3;
        });
    }
    
    const totalBalance = strategyData.members?.reduce((sum, m) => sum + (Number(m.balance) || 0), 0) || 0;
    addBox(
        'COMBINED FUND BALANCE',
        `Total: $${totalBalance.toLocaleString()}`,
        [255, 255, 200]
    );
    
    // Page 3: Investment Objectives
    pdf.addPage();
    currentY = margin;
    
    addHeading('2. INVESTMENT OBJECTIVES');
    addParagraph('The fund aims to achieve the following investment objectives:');
    
    const objectives = [
        `Target Return: ${strategyData.objectives?.targetReturn || 'Not specified'} per annum`,
        `Time Horizon: ${strategyData.objectives?.timeHorizon || 'Not specified'} years`,
        `Maximum Acceptable Drawdown: ${strategyData.objectives?.maxDrawdown || 'Not specified'}%`,
        `Capital Preservation: ${strategyData.objectives?.capitalPreservation ? 'Yes (maintain real value adjusted for inflation)' : 'Not prioritized'}`,
        `Income Generation: ${strategyData.objectives?.incomeGeneration ? `Yes (target $${Number(strategyData.objectives?.targetIncome || 0).toLocaleString()}/year)` : 'Not prioritized'}`
    ];
    
    objectives.forEach(obj => addBullet(obj, 5));
    
    addBox(
        '✅ ATO COMPLIANCE',
        'These objectives are specific, measurable, and appropriate for the members\' circumstances\n' +
        'as required by Regulation 4.09(2)(a) of the SIS Act.',
        [200, 255, 200]
    );
    
    // Page 4: Asset Allocation
    pdf.addPage();
    currentY = margin;
    
    addHeading('3. ASSET ALLOCATION STRATEGY');
    addParagraph('The fund will maintain the following strategic asset allocation:');
    
    if (strategyData.assetAllocation) {
        const allocTableHeaders = ['Asset Class', 'Current %', 'Target Min %', 'Target Max %'];
        const allocTableRows = [];
        
        const assetLabels = {
            ausShares: 'Australian Shares',
            intlShares: 'International Shares',
            property: 'Direct Property',
            bonds: 'Fixed Income / Bonds',
            cash: 'Cash / Term Deposits'
        };
        
        Object.keys(assetLabels).forEach(key => {
            const asset = strategyData.assetAllocation[key];
            if (asset && (asset.current > 0 || asset.targetMax > 0)) {
                allocTableRows.push([
                    assetLabels[key],
                    `${asset.current || 0}%`,
                    `${asset.targetMin || 0}%`,
                    `${asset.targetMax || 0}%`
                ]);
            }
        });
        
        if (allocTableRows.length > 0) {
            addTable(allocTableHeaders, allocTableRows);
        }
    }
    
    addHeading('Rebalancing Policy', 3);
    addParagraph(
        `The fund will review asset allocation ${strategyData.rebalancing?.frequency || 'quarterly'} and ` +
        `rebalance when any asset class deviates more than ${strategyData.rebalancing?.trigger || '5'}% from ` +
        `its target range. Rebalancing will be conducted in a ${strategyData.rebalancing?.method || 'tax-efficient'} manner.`
    );
    
    // Page 5: Liquidity
    pdf.addPage();
    currentY = margin;
    
    addHeading('4. LIQUIDITY MANAGEMENT');
    addParagraph('The fund has assessed its liquidity requirements and will maintain sufficient liquid assets to meet all obligations.');
    
    if (strategyData.liquidity) {
        addHeading('Annual Liquidity Requirements', 3);
        
        const expenses = strategyData.liquidity.operatingExpenses || {};
        const totalExpenses = 
            (Number(expenses.adminFees) || 0) +
            (Number(expenses.auditFees) || 0) +
            (Number(expenses.accountingFees) || 0) +
            (Number(expenses.insurancePremiums) || 0);
        
        const totalPensions = strategyData.members?.reduce((sum, m) => {
            const minRate = m.age >= 65 ? 0.05 : 0.04;
            return sum + ((m.balance || 0) * minRate);
        }, 0) || 0;
        
        const liquidityTableHeaders = ['Requirement', 'Annual Amount'];
        const liquidityTableRows = [
            ['Pension Payments', `$${Math.round(totalPensions).toLocaleString()}`],
            ['Admin Fees', `$${Math.round(expenses.adminFees || 0).toLocaleString()}`],
            ['Audit Fees', `$${Math.round(expenses.auditFees || 0).toLocaleString()}`],
            ['Accounting Fees', `$${Math.round(expenses.accountingFees || 0).toLocaleString()}`],
            ['Insurance Premiums', `$${Math.round(expenses.insurancePremiums || 0).toLocaleString()}`],
            ['TOTAL ANNUAL NEEDS', `$${Math.round(totalExpenses + totalPensions).toLocaleString()}`]
        ];
        
        addTable(liquidityTableHeaders, liquidityTableRows);
        
        addHeading('Liquidity Strategy', 3);
        addParagraph(
            `The fund will maintain a minimum cash reserve of $${Number(strategyData.liquidity.minimumCash || 0).toLocaleString()} ` +
            `(${Math.round((strategyData.liquidity.minimumCash || 0) / ((totalExpenses + totalPensions) / 12 || 1) * 10) / 10} months of expenses).`
        );
    }
    
    // Page 6: Insurance
    pdf.addPage();
    currentY = margin;
    
    addHeading('5. INSURANCE CONSIDERATION');
    addParagraph('The trustees have considered insurance requirements for each member as follows:');
    
    strategyData.members?.forEach((member, idx) => {
        addHeading(`${member.name || `Member ${idx + 1}`} (Age ${member.age})`, 3);
        addParagraph(
            `Life Insurance: Assessment required (document held, external, or not required with justification)\n` +
            `TPD Insurance: Assessment required (document held, external, or not required with justification)`
        );
        currentY += 5;
    });
    
    addBox(
        '⚠️ IMPORTANT',
        'Insurance consideration is MANDATORY under ATO requirements. Even if insurance is not held,\n' +
        'you must document the assessment and reasons for the decision.',
        [255, 245, 200]
    );
    
    // Page 7: Review Schedule
    pdf.addPage();
    currentY = margin;
    
    addHeading('6. REVIEW AND MONITORING');
    
    addHeading('Formal Review Schedule', 3);
    addParagraph(
        `This investment strategy was adopted by trustee resolution on ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}.`
    );
    addParagraph(
        `Next formal review date: ${strategyData.review?.nextReviewDate ? new Date(strategyData.review.nextReviewDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not set'}`
    );
    
    addHeading('Review Triggers', 3);
    addParagraph('The strategy will be reviewed when any of the following occurs:');
    
    if (strategyData.review?.reviewTriggers && strategyData.review.reviewTriggers.length > 0) {
        strategyData.review.reviewTriggers.forEach(trigger => addBullet(trigger, 5));
    } else {
        addBullet('Annual review (mandatory)', 5);
        addBullet('Member circumstances change', 5);
        addBullet('Asset allocation breaches target range', 5);
    }
    
    // Page 8: Trustee Certification
    pdf.addPage();
    currentY = margin;
    
    addHeading('TRUSTEE CERTIFICATION');
    
    addBox(
        'TRUSTEE RESOLUTION',
        'The trustees of this Self-Managed Superannuation Fund hereby adopt this Investment Strategy\n' +
        'and agree to give effect to it in accordance with the requirements of the SIS Act 1993.',
        [255, 235, 235]
    );
    
    currentY += 20;
    addParagraph('Signed by trustees:');
    currentY += 10;
    
    // Signature blocks
    strategyData.members?.forEach((member, idx) => {
        if (currentY > pageHeight - 50) {
            pdf.addPage();
            currentY = margin;
        }
        
        pdf.line(margin, currentY, margin + 80, currentY);
        currentY += 5;
        addParagraph(`${member.name || `Trustee ${idx + 1}`}`);
        addParagraph(`Date: _____________________`);
        currentY += 15;
    });
    
    // Footer on all pages
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
            `${strategyData.fundDetails.fundName || 'SMSF'} Investment Strategy | Page ${i} of ${pageCount} | Generated ${new Date().toLocaleDateString('en-AU')}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
        );
    }
    
    return pdf;
};

// Generate Trustee Minutes Template
export const generateTrusteeMinutesPDF = (strategyData) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const margin = 20;
    let currentY = margin;
    
    // Title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TRUSTEE MEETING MINUTES', pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;
    
    // Meeting details
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    const details = [
        `Fund: ${strategyData.fundDetails.fundName || 'Fund Name'}`,
        `ABN: ${strategyData.fundDetails.abn || 'XXX XXX XXX'}`,
        `Meeting Date: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`,
        `Meeting Time: [To be completed]`,
        `Location: [To be completed]`
    ];
    
    details.forEach(detail => {
        pdf.text(detail, margin, currentY);
        currentY += 8;
    });
    
    currentY += 10;
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('RESOLUTION: ADOPTION OF INVESTMENT STRATEGY', margin, currentY);
    currentY += 10;
    
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    const resolution = [
        'IT WAS RESOLVED THAT:',
        '',
        '1. The Investment Strategy dated ' + new Date().toLocaleDateString('en-AU') + ' be adopted as the fund\'s',
        '   official investment strategy in accordance with Regulation 4.09 of the SIS Act 1993.',
        '',
        '2. The strategy addresses all mandatory elements including:',
        '   • Risk and return objectives',
        '   • Diversification of investments',
        '   • Liquidity requirements',
        '   • Ability to discharge liabilities',
        '   • Insurance needs of members',
        '   • Member circumstances',
        '',
        '3. The strategy will be reviewed annually and when member circumstances change.',
        '',
        '4. All trustees agree to give effect to this investment strategy.'
    ];
    
    resolution.forEach(line => {
        pdf.text(line, margin, currentY);
        currentY += 6;
    });
    
    currentY += 20;
    pdf.text('Trustee Signatures:', margin, currentY);
    currentY += 15;
    
    strategyData.members?.forEach((member, idx) => {
        pdf.line(margin, currentY, margin + 80, currentY);
        currentY += 5;
        pdf.text(`${member.name || `Trustee ${idx + 1}`}`, margin, currentY);
        currentY += 5;
        pdf.text(`Date: _____________________`, margin, currentY);
        currentY += 20;
    });
    
    return pdf;
};

// Generate Review Checklist PDF
export const generateReviewChecklistPDF = (strategyData) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const margin = 20;
    let currentY = margin;
    
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('INVESTMENT STRATEGY REVIEW CHECKLIST', pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;
    
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Next Review Date: ${strategyData.review?.nextReviewDate ? new Date(strategyData.review.nextReviewDate).toLocaleDateString('en-AU') : '________'}`, margin, currentY);
    currentY += 15;
    
    const checklist = [
        { item: 'Review member circumstances (age, employment, retirement status)', checked: false },
        { item: 'Assess if investment objectives still appropriate', checked: false },
        { item: 'Check actual asset allocation vs. target ranges', checked: false },
        { item: 'Confirm adequate liquidity for obligations', checked: false },
        { item: 'Reassess insurance needs', checked: false },
        { item: 'Review investment performance vs. objectives', checked: false },
        { item: 'Check compliance with investment restrictions', checked: false },
        { item: 'Verify all transactions at arm\'s length', checked: false },
        { item: 'Confirm no breaches of in-house asset rules (<5%)', checked: false },
        { item: 'Update strategy if circumstances changed', checked: false },
        { item: 'Document review in trustee minutes', checked: false },
        { item: 'Set next review date', checked: false }
    ];
    
    checklist.forEach(({ item, checked }) => {
        pdf.rect(margin, currentY - 3, 4, 4);
        pdf.text(item, margin + 7, currentY);
        currentY += 7;
    });
    
    return pdf;
};

export default {
    generateInvestmentStrategyPDF,
    generateTrusteeMinutesPDF,
    generateReviewChecklistPDF
};

